import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";

/**
 * A onda do que esta sendo gravado, correndo para a esquerda.
 *
 * A primeira versao desenhava o espectro do instante: todas as barras mudavam
 * juntas a cada quadro e, no silencio, aquilo virava uma fileira de pontinhos
 * que nao dizia nada. O WhatsApp faz diferente, e faz melhor: cada barra e um
 * PEDACO DO PASSADO. A mais nova entra pela direita, as antigas caminham para a
 * esquerda e saem. Quem olha ve o desenho do que acabou de falar — da para
 * reconhecer as pausas, e da para perceber na hora se o microfone nao captou.
 */
const BARRAS = 30;
const INTERVALO_MS = 80;
const ALTURA_MAX = 22;
const ALTURA_MIN = 2;

const useStyles = makeStyles((theme) => ({
  caixa: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
    height: ALTURA_MAX + 4,
    marginLeft: 6,
    marginRight: 4,
    // Largura fixa: a barra divide a linha com o campo de texto, e deixar ela
    // crescer empurrava o cronometro e os botoes para fora em tela estreita.
    width: 118,
    flexShrink: 0,
    overflow: "hidden"
  },
  barra: {
    width: 2,
    height: ALTURA_MIN,
    borderRadius: 2,
    backgroundColor: "#0D9488",
    flexShrink: 0,
    // A transicao tem a mesma duracao da amostragem: cada barra chega na altura
    // nova bem na hora em que a proxima e sorteada, e o conjunto lê como um
    // movimento continuo em vez de pulos.
    transition: `height ${INTERVALO_MS}ms linear`
  }
}));

const OndasDaGravacao = ({ stream }) => {
  const classes = useStyles();
  const barrasRef = useRef([]);
  const historicoRef = useRef(new Array(BARRAS).fill(0));

  useEffect(() => {
    if (!stream) return undefined;

    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!Contexto) return undefined;

    let contexto;
    let fonte;
    let analisador;
    let animacao;

    try {
      contexto = new Contexto();
      fonte = contexto.createMediaStreamSource(stream);
      analisador = contexto.createAnalyser();
      analisador.fftSize = 1024;

      // De proposito NAO ligamos no destination: ligar devolveria a propria voz
      // pela caixa de som e viraria microfonia.
      fonte.connect(analisador);

      if (contexto.state === "suspended") contexto.resume();
    } catch (e) {
      // Navegador sem Web Audio, ou permissao revogada no meio: a gravacao
      // continua funcionando, so fica sem a animacao.
      return undefined;
    }

    const amostras = new Uint8Array(analisador.fftSize);
    let ultimaAmostra = 0;

    // Volume real do momento, pela onda no tempo (nao pelo espectro): e isto
    // que faz a barra acompanhar a voz, e nao o timbre dela.
    const volumeAgora = () => {
      analisador.getByteTimeDomainData(amostras);

      let soma = 0;
      for (let i = 0; i < amostras.length; i += 1) {
        const desvio = (amostras[i] - 128) / 128;
        soma += desvio * desvio;
      }

      return Math.sqrt(soma / amostras.length);
    };

    /**
     * Volume medido -> altura da barra, em decibeis.
     *
     * Multiplicar o RMS por um ganho fixo nao funciona, e foi o erro da versao
     * anterior: fala normal fica na casa de 0,05 de RMS e grito passa de 0,3.
     * Numa regua linear, tudo que nao e grito mora nos primeiros pixels — a
     * onda so reagia quando a pessoa levantava a voz.
     *
     * Ouvido humano nao e linear, e a regua aqui tambem nao pode ser. Em dB a
     * fala normal cai perto do meio da escala e sobra espaco para os dois
     * lados: da para ver a diferenca entre falar baixo e falar normal, e ainda
     * ver o pico quando alguem levanta a voz.
     *
     *   silencio (-55 dB) -> 0        fala baixa (-40 dB) -> 0,33
     *   fala normal (-26 dB) -> 0,64  grito (-10 dB) -> 1
     */
    const DB_MUDO = -55;
    const DB_CHEIO = -10;

    const nivelDaVoz = (rms) => {
      const db = 20 * Math.log10(rms + 1e-6);
      const proporcao = (db - DB_MUDO) / (DB_CHEIO - DB_MUDO);
      return Math.min(1, Math.max(0, proporcao));
    };

    const desenhar = (agora) => {
      if (agora - ultimaAmostra >= INTERVALO_MS) {
        ultimaAmostra = agora;

        historicoRef.current.shift();
        historicoRef.current.push(nivelDaVoz(volumeAgora()));

        for (let i = 0; i < BARRAS; i += 1) {
          const elemento = barrasRef.current[i];
          if (!elemento) continue;

          const altura = Math.max(
            ALTURA_MIN,
            Math.round(historicoRef.current[i] * ALTURA_MAX)
          );

          elemento.style.height = `${altura}px`;
        }
      }

      animacao = requestAnimationFrame(desenhar);
    };

    animacao = requestAnimationFrame(desenhar);

    return () => {
      if (animacao) cancelAnimationFrame(animacao);
      historicoRef.current = new Array(BARRAS).fill(0);

      try {
        fonte.disconnect();
      } catch (e) {
        /* ja desconectado */
      }
      try {
        // O stream e do gravador: quem desliga o microfone e ele, nao nos.
        contexto.close();
      } catch (e) {
        /* ja fechado */
      }
    };
  }, [stream]);

  return (
    <div className={classes.caixa} aria-hidden="true">
      {Array.from({ length: BARRAS }).map((_, i) => (
        <div
          key={i}
          ref={(el) => {
            barrasRef.current[i] = el;
          }}
          className={classes.barra}
        />
      ))}
    </div>
  );
};

export default OndasDaGravacao;
